import { useState } from 'react';
// @mui
import {
    Box,
    Container,
    Select,
    MenuItem,
    Checkbox,
    Alert,
    Card,
    Stack,
    Typography,
    FormControlLabel,
    Button
} from '@mui/material';
import Chip from '@mui/material/Chip';

// components
import Page from '../components/Page';
// sections
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import languages from '../services/languages'
import {updateUser} from "../services/firebaseService";
import {useDispatch, useSelector} from "react-redux";
import * as actions from "../store/actions";
import Iconify from '../components/Iconify';
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
export default function Settings() {
  const { t } = useLanguage();
  const { currentUser } = useAuth();
  const dispatch = useDispatch();
  const settingTitle = t('setting'); // Settings
  const languageSelectionLabel = t('settings.langSelect'); // What language do you speak ?
  const languageTranslation = t('st.translation') || 'Auto-Translate spoken language into another language'; // Auto-Translate spoken language into another language
  const labelSelectTargetLanguage = t('st.targetLanguage'); // Select target language
  const [lang, setLang] = useState('Afrikaans (Suid-Afrika)');
  const preferredLangs = useSelector((state) => state.pLanguage.data);
  const doTranslate = useSelector((state) => state.pLanguage.translate);
  const targetLang = useSelector((state) => state.pLanguage.targetLanguage);

  const handleLanguageSelect = (e) => {
    setLang(e.target.value)
    if (!preferredLangs.includes(e.target.value)) {
       const updatedLangs = [e.target.value, ...preferredLangs]
       dispatch({type: actions.PREFERRED_LANGUAGE_CHANGE_SUCCESS, data: updatedLangs})
       updateUser(currentUser.email, {preferredLang: updatedLangs})
    }
  }
  const handleTargetLanguageSelect = (e) => {
    if (doTranslate) {
        dispatch({type: actions.TARGET_LANGUAGE_CHANGE_SUCCESS, targetLanguage: e.target.value})
        updateUser(currentUser.email, {targetLanguage: e.target.value})
            .then(() => {
                window.location.reload()
            })
    }
  }
  const handleDelete = (chipToDelete) => () => {
    const updatedLangs = preferredLangs.filter((chip) => chip !== chipToDelete);
    dispatch({type: actions.PREFERRED_LANGUAGE_CHANGE_SUCCESS, data: updatedLangs})
    updateUser(currentUser.email, {preferredLang: updatedLangs})
  };

  const changeDoTranslate = (e) => {
    updateUser(currentUser.email, {translate: !doTranslate})
    dispatch({type: actions.TARGET_LANGUAGE_STATUS_CHANGE_SUCCESS, translate: !doTranslate})
  }

  return (
    <Page title={settingTitle}>
        <Container maxWidth="lg">
            <Stack spacing={3}>
                <Box>
                    <Typography variant="h3">Voice settings</Typography>
                    <Typography sx={{ color: 'text.secondary', mt: 1 }}>
                        Keep the voice input defaults simple: choose what you speak, what gets typed, and when translation is enabled.
                    </Typography>
                </Box>

                <Card sx={{ p: { xs: 2.5, md: 3 }, border: (theme) => `1px solid ${theme.palette.divider}` }}>
                    <Stack spacing={3}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Box
                                sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 2,
                                    bgcolor: 'secondary.lighter',
                                    color: 'secondary.dark',
                                    display: 'grid',
                                    placeItems: 'center',
                                }}
                            >
                                <Iconify icon="fa-solid:language" width={22} height={22} />
                            </Box>
                            <Box>
                                <Typography variant="h5">{languageSelectionLabel}</Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Add languages you use often. They will appear as quick choices while dictating.
                                </Typography>
                            </Box>
                        </Stack>

                        <Select
                          onChange={handleLanguageSelect}
                          value={lang}
                          label="Select Language"
                          sx={{ maxWidth: 360 }}
                        >
                            {Object.keys(languages).map((langName) => (
                                <MenuItem key={langName} value={langName}>
                                    {langName}
                                </MenuItem>
                              ))}
                        </Select>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                          {preferredLangs.map((data) => (
                            <Chip
                              key={data}
                              label={data}
                              onDelete={handleDelete(data)}
                              sx={{ bgcolor: 'background.neutral', fontWeight: 600 }}
                            />
                          ))}
                        </Stack>
                    </Stack>
                </Card>

                <Card sx={{ p: { xs: 2.5, md: 3 }, border: (theme) => `1px solid ${theme.palette.divider}` }}>
                    <Stack spacing={3}>
                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                            <Box>
                                <Typography variant="h5">Translation</Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                    {languageTranslation}
                                </Typography>
                            </Box>
                            <FormControlLabel
                              control={<Checkbox onChange={changeDoTranslate} checked={doTranslate} />}
                              label={doTranslate ? 'Enabled' : 'Disabled'}
                            />
                        </Stack>

                        {doTranslate && (
                            <Stack spacing={2} alignItems="flex-start">
                                <Select
                                    onChange={handleTargetLanguageSelect}
                                    value={targetLang || labelSelectTargetLanguage}
                                    label="Select Language"
                                    sx={{ minWidth: 260, maxWidth: 360 }}
                                >
                                    <MenuItem key={labelSelectTargetLanguage} value={labelSelectTargetLanguage} selected disabled>
                                        {labelSelectTargetLanguage}
                                    </MenuItem>
                                    {Object.keys(languages).map((langName) => (
                                        <MenuItem key={langName} value={langName}>
                                            {langName}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                                    Translation is experimental and can be slower than same-language dictation.
                                </Alert>
                            </Stack>
                        )}

                        {!doTranslate && (
                            <Button variant="outlined" onClick={changeDoTranslate} sx={{ alignSelf: 'flex-start' }}>
                                Turn on translation
                            </Button>
                        )}
                    </Stack>
                </Card>
            </Stack>
        </Container>
    </Page>
  );
}
